import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/LanguageSwitcher';

/**
 * Header component for the website
 * Features responsive navigation, logo, call-to-action button, and language switcher
 * Uses a gaming-inspired design with animations
 */
const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [window.location.pathname]);
  
  return (
    <header 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-primary shadow-lg py-3' 
          : 'bg-primary/90 backdrop-blur-sm py-5'
      }`}
    >
      {/* Gaming-inspired decorative element */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-accent"></div>
      
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="relative z-10">
            <div className="flex items-center">
              <img 
                src="/natux-logo.png" 
                alt="NatuX Translations Logo" 
                className="h-10 md:h-12"
              />
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <NavLink 
              to="/" 
              className={({ isActive }) => 
                `text-white/90 hover:text-accent transition-colors duration-300 ${
                  isActive ? 'text-accent' : ''
                }`
              }
            >
              {t('navigation.home')}
            </NavLink>
            <NavLink 
              to="/services" 
              className={({ isActive }) => 
                `text-white/90 hover:text-accent transition-colors duration-300 ${
                  isActive ? 'text-accent' : ''
                }`
              }
            >
              {t('navigation.services')}
            </NavLink>
            <NavLink 
              to="/portfolio" 
              className={({ isActive }) => 
                `text-white/90 hover:text-accent transition-colors duration-300 ${
                  isActive ? 'text-accent' : ''
                }`
              }
            >
              {t('navigation.portfolio')}
            </NavLink>
            <NavLink 
              to="/testimonials" 
              className={({ isActive }) => 
                `text-white/90 hover:text-accent transition-colors duration-300 ${
                  isActive ? 'text-accent' : ''
                }`
              }
            >
              {t('navigation.testimonials')}
            </NavLink>
            <NavLink 
              to="/contact" 
              className={({ isActive }) => 
                `text-white/90 hover:text-accent transition-colors duration-300 ${
                  isActive ? 'text-accent' : ''
                }`
              }
            >
              {t('navigation.contact')}
            </NavLink>
            
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* CTA Button */}
            <Link 
              to="/contact" 
              className="bg-accent text-primary px-5 py-2 rounded-md shadow-md hover:shadow-accent/30 hover:bg-white transition-all duration-300 ml-4 font-medium"
            >
              {t('navigation.getQuote')}
            </Link>
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white text-xl focus:outline-none z-50"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu - Only rendered when open */}
      {mobileMenuOpen && (
        <div 
          className="fixed top-0 left-0 right-0 bottom-0 z-[100]" 
          data-lenis-prevent
        >
          {/* Backdrop overlay */}
          <div className="absolute inset-0 bg-black opacity-70"></div>
          
          {/* Menu content */}
          <div 
            className="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center justify-center p-6"
            style={{ backgroundColor: '#3B4CCA' }}
          >
            {/* Gaming-inspired decorative elements */}
            <div className="absolute top-20 right-10 w-6 h-6 bg-secondary rotate-45 opacity-20"></div>
            <div className="absolute bottom-20 left-10 w-4 h-4 bg-accent opacity-20"></div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-accent"></div>
            
            {/* Logo in mobile menu */}
            <div className="mb-10">
              <img 
                src="/natux-logo.png" 
                alt="NatuX Translations Logo" 
                className="h-12"
              />
            </div>
            
            <nav className="flex flex-col space-y-8 items-center w-full px-6 relative z-[110]">
              <MobileNavLink to="/">{t('navigation.home')}</MobileNavLink>
              <MobileNavLink to="/services">{t('navigation.services')}</MobileNavLink>
              <MobileNavLink to="/portfolio">{t('navigation.portfolio')}</MobileNavLink>
              <MobileNavLink to="/testimonials">{t('navigation.testimonials')}</MobileNavLink>
              <MobileNavLink to="/contact">{t('navigation.contact')}</MobileNavLink>
              
              {/* Mobile Language Switcher */}
              <div className="w-full flex justify-center my-4">
                <LanguageSwitcher />
              </div>
              
              {/* CTA Button */}
              <Link 
                to="/contact" 
                className="bg-accent text-primary px-8 py-3 rounded-md shadow-md hover:shadow-accent/30 hover:bg-white transition-all duration-300 mt-4 font-medium w-full max-w-xs text-center"
              >
                {t('navigation.getQuote')}
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

// Helper component for mobile navigation links
const MobileNavLink = ({ to, children }) => (
  <NavLink 
    to={to} 
    className={({ isActive }) => 
      `text-white text-xl hover:text-accent transition-colors duration-300 w-full text-center ${
        isActive ? 'text-accent' : ''
      }`
    }
  >
    {children}
  </NavLink>
);

export default Header;
